/**
 * Chakra-pad (§2): verticaal pad van wortel (onder) naar kroon (boven), 7 knopen,
 * locked/unlocked/voltooid. Vaste menu-knoppen Reisdagboek & Archief.
 */
import { CHAKRA_VOLGORDE } from '../data/chakras';
import { useProgressStore, isOntgrendeld, voortgangWordtBewaard } from '../store/progressStore';
import { useRunStore } from '../store/runStore';
import { useBattleStore } from '../store/battleStore';
import { t, type Taal } from '../i18n';

interface Props {
  taal: Taal;
  onReisdagboek: () => void;
  onArchief: () => void;
  onCodex: () => void;
  onInstellingen: () => void;
}

export function ChakraPad({ taal, onReisdagboek, onArchief, onCodex, onInstellingen }: Props) {
  const voltooid = useProgressStore((s) => s.voltooideChakras);
  const startChakra = useRunStore((s) => s.startChakra);
  const zetTaal = useBattleStore((s) => s.zetTaal);

  return (
    <div className="mandala-textuur flex h-full w-full flex-col bg-achtergrond text-slate-100">
      {/* topbalk */}
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
        <h1 className="text-lg font-bold tracking-wide" style={{ fontFamily: 'Cinzel, serif', color: '#c9a86a' }}>Chakra Architect</h1>
        <div className="flex items-center gap-2 text-sm">
          <button type="button" onClick={onCodex} className="rounded border border-white/15 px-3 py-1 hover:bg-white/5">{t('ui.codex', taal)}</button>
          <button type="button" onClick={onReisdagboek} className="rounded border border-white/15 px-3 py-1 hover:bg-white/5">{t('ui.reisdagboek', taal)}</button>
          <button type="button" onClick={onArchief} className="rounded border border-white/15 px-3 py-1 hover:bg-white/5">{t('ui.archief', taal)}</button>
          <button type="button" onClick={onInstellingen} className="rounded border border-white/15 px-2 py-1 hover:bg-white/5" title={t('ui.instellingen', taal)}>⚙</button>
          <span className="ml-1 flex gap-1 text-xs text-slate-500">
            <button type="button" onClick={() => zetTaal('nl')} className={taal === 'nl' ? 'text-slate-100 underline' : ''}>NL</button>
            <button type="button" onClick={() => zetTaal('en')} className={taal === 'en' ? 'text-slate-100 underline' : ''}>EN</button>
          </span>
        </div>
      </div>

      {!voortgangWordtBewaard && (
        <div className="bg-amber-500/10 px-4 py-1 text-center text-[11px] text-amber-300">{t('ui.geenSave', taal)}</div>
      )}

      {/* pad (kroon boven → wortel onder) */}
      <div className="flex flex-1 flex-col items-center justify-center gap-1 overflow-y-auto py-4">
        {[...CHAKRA_VOLGORDE].reverse().map((c) => {
          const n = c.header.nummer;
          const ontgrendeld = isOntgrendeld(n, voltooid);
          const klaar = voltooid.includes(n);
          return (
            <div key={n} className="flex flex-col items-center">
              <button
                type="button"
                disabled={!ontgrendeld}
                onClick={() => startChakra(n)}
                className={[
                  'flex w-72 items-center gap-3 rounded-2xl border px-4 py-2 transition',
                  ontgrendeld ? 'cursor-pointer hover:scale-[1.03]' : 'cursor-not-allowed opacity-40',
                ].join(' ')}
                style={{ borderColor: c.header.accent, background: `linear-gradient(120deg, #14121c, ${c.header.kleur}33)` }}
              >
                {/* bloembladring-icoon (vereenvoudigd) */}
                <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full" style={{ background: `radial-gradient(circle, ${c.header.kleur}, ${c.header.kleur}55)` }}>
                  <BloembladRing aantal={Math.min(c.header.bloemblaadjes, 16)} kleur={c.header.accent} />
                  <span className="relative text-xs font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{n}</span>
                </span>
                <span className="flex-1 text-left">
                  <span className="block font-semibold" style={{ fontFamily: 'Cinzel, serif', color: '#ece6da' }}>{c.header.naam}</span>
                  <span className="block text-[11px] text-slate-400">{c.header.element} · {c.header.mantra} · {c.header.resourceLabel}</span>
                </span>
                <span className="text-lg" style={{ color: c.header.accent }}>{klaar ? '✦' : ontgrendeld ? '›' : '🔒'}</span>
              </button>
              {n > 1 && <span className="h-4 w-px" style={{ background: 'rgba(255,255,255,0.15)' }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Eenvoudige SVG-bloembladring (n stippen rond het midden). */
function BloembladRing({ aantal, kleur }: { aantal: number; kleur: string }) {
  const r = 18;
  return (
    <svg viewBox="-24 -24 48 48" className="absolute inset-0 h-full w-full">
      {Array.from({ length: aantal }).map((_, i) => {
        const a = (i / aantal) * Math.PI * 2;
        return <circle key={i} cx={Math.cos(a) * r} cy={Math.sin(a) * r} r={2.2} fill={kleur} opacity={0.8} />;
      })}
    </svg>
  );
}
